/**
 * Locale-aware formatting that follows the active UI language (i18n.language).
 *
 *   formatDate('2026-08-21')            → "21/08/2026"  (fr) · "21/08/2026" (en-GB)
 *   formatDateTime(new Date())          → "21/08/2026 09:30"
 *   formatNumber(1234.5)                → "1 234,5"     (fr) · "1,234.5"    (en)
 *   formatPercent(0.153)                → "15,3 %"      (fr) · "15.3%"      (en)
 *   formatMoneyUi(1234.5, 'EUR')        → "1 234,50 €"  (fr) · "€1,234.50"  (en)
 *
 * Money: `utils/formatMoney.js` stays THE money formatter. By default it infers
 * the locale from the currency (contract GROUP_REPORTING_CONTRACT.md); `formatMoneyUi`
 * is the thin wrapper that passes the UI locale instead — use it when the number
 * style must follow the reader, not the currency.
 *
 * In components prefer `useFormat()` — it re-renders on language change.
 */

import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from './index.js';
import { formatMoney } from '../utils/formatMoney.js';

const cache = new Map();

function getFormatter(kind, locale, options) {
  const key = `${kind}|${locale}|${JSON.stringify(options)}`;
  let fmt = cache.get(key);
  if (!fmt) {
    fmt = kind === 'date' ? new Intl.DateTimeFormat(locale, options) : new Intl.NumberFormat(locale, options);
    cache.set(key, fmt);
  }
  return fmt;
}

function toDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const DATE_STYLES = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  monthYear: { month: 'long', year: 'numeric' },
};

export function formatDate(value, style = 'short', locale = currentLocale()) {
  const d = toDate(value);
  if (!d) return '—';
  const options = typeof style === 'string' ? DATE_STYLES[style] || DATE_STYLES.short : style;
  return getFormatter('date', locale, options).format(d);
}

export function formatDateTime(value, locale = currentLocale()) {
  const d = toDate(value);
  if (!d) return '—';
  return getFormatter('date', locale, { ...DATE_STYLES.short, hour: '2-digit', minute: '2-digit' }).format(d);
}

export function formatTime(value, locale = currentLocale()) {
  const d = toDate(value);
  if (!d) return '—';
  return getFormatter('date', locale, { hour: '2-digit', minute: '2-digit' }).format(d);
}

export function formatNumber(value, { decimals, locale = currentLocale() } = {}) {
  const n = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value);
  if (!Number.isFinite(n)) return '—';
  const options = decimals == null ? { maximumFractionDigits: 2 } : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  return getFormatter('number', locale, options).format(n);
}

/** `ratio` is 0..1 (0.153 → "15,3 %"). */
export function formatPercent(ratio, { decimals = 1, locale = currentLocale() } = {}) {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return '—';
  return getFormatter('number', locale, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
}

/** Money in the reader's locale (see header comment). */
export function formatMoneyUi(amount, currency = 'EUR', options = {}) {
  return formatMoney(amount, currency, { ...options, locale: options.locale || currentLocale() });
}

/** Relative date in words: "il y a 3 jours" / "3 days ago". */
export function formatRelative(value, locale = currentLocale()) {
  const d = toDate(value);
  if (!d) return '—';
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / (86400 * 30)), 'month');
  return rtf.format(Math.round(diffSec / (86400 * 365)), 'year');
}

/**
 * Hook: same helpers, bound to the current language, re-rendering on switch.
 *   const { t, fmt } = useFormat();  fmt.date(x) · fmt.dateTime(x) · fmt.number(x) · fmt.money(x, 'PLN')
 */
export function useFormat() {
  const { t, i18n: instance } = useTranslation();
  const locale = currentLocale();
  return {
    t,
    language: instance.language,
    locale,
    fmt: {
      date: (v, style) => formatDate(v, style, locale),
      dateTime: (v) => formatDateTime(v, locale),
      time: (v) => formatTime(v, locale),
      number: (v, opts) => formatNumber(v, { ...opts, locale }),
      percent: (v, opts) => formatPercent(v, { ...opts, locale }),
      money: (amount, currency, opts) => formatMoneyUi(amount, currency, { ...opts, locale }),
      relative: (v) => formatRelative(v, locale),
    },
  };
}

export { i18n, currentLocale };
